import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "./api";

export interface GatewayEvent {
  type: string;
  data: Record<string, unknown>;
  at: string;
}

/**
 * Subscribes to the server's SSE feed and invalidates the affected react-query caches, so a
 * status change on the phone shows up in the dashboard without a refresh.
 *
 * EventSource cannot send an Authorization header, which is exactly why the API also accepts
 * the httpOnly `accessToken` cookie — `withCredentials` is what makes this authenticate.
 *
 * Invalidating rather than patching the cache in place is deliberate: the events carry a
 * summary, not the full row, and a half-applied patch is harder to reason about than one extra
 * fetch of data the user is already looking at.
 */
export function useGatewayEvents(onEvent?: (event: GatewayEvent) => void): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const source = new EventSource(`${API_BASE_URL}/events`, { withCredentials: true });

    source.onmessage = (raw) => {
      let event: GatewayEvent;
      try {
        event = JSON.parse(raw.data) as GatewayEvent;
      } catch {
        return;
      }
      if (event.type === "ping") return;

      switch (event.type) {
        case "message.status.changed":
        case "message.received":
          void queryClient.invalidateQueries({ queryKey: ["messages"] });
          void queryClient.invalidateQueries({ queryKey: ["stats"] });
          // Threaded views read from their own key. Invalidating it here is what makes a reply
          // land in an open conversation live, over the SAME EventSource -- no second stream.
          void queryClient.invalidateQueries({ queryKey: ["conversations"] });
          break;
        case "device.online":
        case "device.offline":
          void queryClient.invalidateQueries({ queryKey: ["devices"] });
          break;
        case "quota.updated":
          void queryClient.invalidateQueries({ queryKey: ["stats"] });
          break;
      }

      onEvent?.(event);
    };

    // The browser reconnects on its own after an error, so this only needs to avoid noisy
    // logging — closing here would defeat that built-in retry.
    source.onerror = () => undefined;

    return () => source.close();
    // onEvent is intentionally excluded: callers pass inline closures, and re-subscribing on
    // every render would tear down and rebuild the stream continuously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);
}
